import { useQuery } from '@tanstack/react-query';
import { ActualServer, Account, API_BASE_URL } from '../types';

/**
 * Custom hook for loading accounts using React Query with persistent caching
 * 
 * Benefits over direct fetch calls:
 * - ✅ Automatic request deduplication (multiple components requesting same data = 1 API call)
 * - ✅ Intelligent retry logic with exponential backoff (prevents spam requests)
 * - ✅ Built-in caching with configurable stale time (2 minutes) and persistence (24 hours)
 * - ✅ Background refetching to keep data fresh (every 5 minutes)
 * - ✅ Loading and error state management
 * - ✅ Prevents multiple concurrent requests to the same endpoint
 * - ✅ Handles network errors gracefully without overwhelming the server
 * - ✅ PERSISTENT CACHE: Survives browser restarts and page refreshes
 * - ✅ BACKGROUND UPDATES: Automatically updates data in background
 * - ✅ OFFLINE FIRST: Works even when network is unavailable
 * - ✅ WINDOW FOCUS REFETCH: Updates data when user returns to tab
 * - ✅ NETWORK RECONNECT: Refetches when internet connection is restored
 */

interface UseAccountsProps {
  server: ActualServer | null;
  budgetId?: string;
  budgetPassword?: string;
  enabled?: boolean;
}

interface UseAccountsReturn {
  accounts: Account[];
  isLoading: boolean;
  error: string | null;
  isRefetching: boolean;
}

const fetchAccounts = async (
  server: ActualServer, 
  budgetId: string, 
  budgetPassword?: string
): Promise<Account[]> => {
  console.log(`🔄 [React Query Persistent] Fetching accounts for server: ${server.name}, budget: ${budgetId}`);
  
  const requestBody: any = {
    serverURL: server.url,
    password: server.password,
    budgetId: budgetId
  };

  if (budgetPassword) {
    requestBody.budgetPassword = budgetPassword;
  }

  const response = await fetch(`${API_BASE_URL}/get-accounts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch (parseError) {
      console.error(`❌ [React Query Persistent] Server error: ${response.status} ${response.statusText}`);
      throw new Error(`Server error: ${response.status} ${response.statusText}`);
    }
    
    if (errorData.needsBudgetPassword) {
      console.error(`🔒 [React Query Persistent] Budget password required`);
      throw new Error('NEEDS_PASSWORD');
    }
    
    console.error(`❌ [React Query Persistent] API error: ${errorData.error}`);
    throw new Error(errorData.error || 'Failed to get accounts');
  }

  const data = await response.json();
  console.log(`✅ [React Query Persistent] Successfully fetched ${data.accounts.length} accounts - will be cached and persisted`);
  return data.accounts;
};

export const useAccounts = ({ 
  server, 
  budgetId, 
  budgetPassword, 
  enabled = true 
}: UseAccountsProps): UseAccountsReturn => {
  const {
    data: accounts = [],
    isLoading,
    error,
    isRefetching
  } = useQuery({
    queryKey: ['accounts', server?.id, budgetId, budgetPassword],
    queryFn: () => fetchAccounts(server!, budgetId!, budgetPassword),
    enabled: enabled && !!server && !!budgetId,
    staleTime: 2 * 60 * 1000, // 2 minutes - data considered fresh
    gcTime: 24 * 60 * 60 * 1000, // 24 hours - persist across browser sessions
    refetchOnWindowFocus: true, // Always refetch when user returns to tab
    refetchOnReconnect: true, // Refetch when network comes back
    refetchInterval: 5 * 60 * 1000, // Background refetch every 5 minutes
    refetchIntervalInBackground: true, // Keep refetching even when tab is hidden
    networkMode: 'offlineFirst', // Use cache first, then try network
    retry: (failureCount, error) => {
      console.log(`🔄 [React Query Persistent] Retry attempt ${failureCount + 1} for error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      
      // Don't retry if it's a password error
      if (error instanceof Error && error.message === 'NEEDS_PASSWORD') {
        console.log(`🚫 [React Query Persistent] Not retrying password error`);
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => {
      const delay = Math.min(1000 * 2 ** attemptIndex, 30000);
      console.log(`⏳ [React Query Persistent] Waiting ${delay}ms before retry ${attemptIndex + 1}`);
      return delay;
    },
  });

  return {
    accounts,
    isLoading,
    error: error instanceof Error ? error.message : null,
    isRefetching
  };
}; 